/**
 * AdminInvitesManager — /admin/invites
 *
 * Mint, list, and revoke admin (or Pro) invite links. Each accepted
 * invite either creates a new account already at the target role, or
 * promotes an existing account with the same email.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  ShieldCheck, Mail, Copy, XCircle, Loader2, Send, Sparkles, Clock,
} from 'lucide-react';

const STATUS_STYLES = {
  pending:  'bg-primary/10 text-primary',
  accepted: 'bg-emerald-500/15 text-emerald-700',
  revoked:  'bg-rose-500/15 text-rose-600',
  expired:  'bg-muted text-muted-foreground',
};

const AdminInvitesManager = () => {
  const { API } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('admin');
  const [days, setDays] = useState(7);
  const [note, setNote] = useState('');
  const [minting, setMinting] = useState(false);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUrl, setLastUrl] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/invites?limit=200`);
      setInvites(r.data.invites || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load invites');
    } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const mint = async (e) => {
    e && e.preventDefault();
    if (!email.trim()) { toast.error('Enter an email'); return; }
    setMinting(true);
    try {
      const r = await axios.post(`${API}/admin/invites`, {
        email: email.trim(),
        role,
        expires_in_days: Number(days) || 7,
        note: note.trim() || null,
      });
      setLastUrl(r.data.invite_url);
      toast.success(
        r.data.reused
          ? 'Existing pending invite reused'
          : `Invite minted for ${email.trim()}`
      );
      setEmail(''); setNote('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create invite');
    } finally { setMinting(false); }
  };

  const revoke = async (invite) => {
    if (!window.confirm(`Revoke invite for ${invite.email}?`)) return;
    try {
      await axios.delete(`${API}/admin/invites/${invite.id}`);
      toast.success('Invite revoked');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not revoke');
    }
  };

  const copy = (url) => {
    navigator.clipboard?.writeText(url);
    toast.success('Invite link copied');
  };

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="admin-invites-manager">
      <div className="mb-4">
        <Link to="/admin/portal" className="inline-flex items-center gap-1 text-xs font-bold text-muted-foreground hover:text-foreground">
          ← Admin portal
        </Link>
      </div>

      <div className="pro-card p-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-2xl bg-primary/15 text-primary flex items-center justify-center">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Admin invites</div>
            <h1 className="font-heading text-2xl font-extrabold">Invite a co-founder or moderator</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Mint a one-time link. When the recipient opens it, we auto-provision
              their account at the target role (or promote it if they already have one).
              No manual role change afterwards.
            </p>
          </div>
        </div>

        <form onSubmit={mint} className="mt-5 grid grid-cols-1 md:grid-cols-6 gap-3">
          <div className="md:col-span-3">
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Email</label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="cofounder@example.com"
                className="pl-9"
                data-testid="admin-invite-email"
                required
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="admin-invite-role"
            >
              <option value="admin">Admin</option>
              <option value="pro">Pro (comp)</option>
              <option value="free">Free</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Expires (days)</label>
            <Input
              type="number" min={1} max={60}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              data-testid="admin-invite-days"
            />
          </div>
          <div className="md:col-span-6">
            <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Note (optional)</label>
            <Textarea
              rows={1}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Co-founder — full access"
              data-testid="admin-invite-note"
            />
          </div>
          <div className="md:col-span-6">
            <Button
              type="submit"
              disabled={minting}
              className="rounded-full font-bold"
              data-testid="admin-invite-submit-btn"
            >
              {minting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Minting…</> : <><Send className="w-4 h-4 mr-2" /> Mint invite link</>}
            </Button>
          </div>
        </form>

        {lastUrl && (
          <div className="mt-4 rounded-xl border-2 border-primary/30 bg-primary/5 p-3 flex items-center justify-between gap-2" data-testid="admin-invite-last-url">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Just minted — share this link</div>
              <div className="font-mono text-xs break-all mt-1">{lastUrl}</div>
            </div>
            <Button type="button" size="sm" variant="outline" onClick={() => copy(lastUrl)} className="rounded-full flex-shrink-0">
              <Copy className="w-3 h-3 mr-1" /> Copy
            </Button>
          </div>
        )}
      </div>

      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">All invites</div>
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="pro-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-[10px] uppercase tracking-widest font-bold">
                <tr>
                  <th className="text-left px-4 py-2">Email</th>
                  <th className="text-left px-4 py-2">Role</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Expires</th>
                  <th className="text-left px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {invites.length === 0 ? (
                  <tr><td colSpan={5} className="text-center italic text-muted-foreground py-6">No invites yet</td></tr>
                ) : invites.map((i) => (
                  <tr key={i.id} className="border-t border-border" data-testid={`invite-row-${i.id}`}>
                    <td className="px-4 py-2">
                      <div className="font-semibold text-foreground">{i.email}</div>
                      {i.note && <div className="text-[11px] text-muted-foreground">{i.note}</div>}
                    </td>
                    <td className="px-4 py-2">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-muted/70 px-2 py-0.5 rounded-full">
                        {i.role === 'admin' && <Sparkles className="w-3 h-3" />} {i.role}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${STATUS_STYLES[i.status] || 'bg-muted'}`}>
                        {i.status === 'pending' && <Clock className="w-3 h-3" />}
                        {i.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {i.expires_at ? new Date(i.expires_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {i.status === 'pending' ? (
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => copy(i.invite_url)} className="rounded-full h-7 px-2" data-testid={`invite-copy-${i.id}`}>
                            <Copy className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => revoke(i)} className="rounded-full h-7 px-2 text-rose-600 border-rose-300" data-testid={`invite-revoke-${i.id}`}>
                            <XCircle className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-[11px] text-muted-foreground italic">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInvitesManager;
